// src/App.jsx
// FINAL USER-FRIENDLY VERSION - SERVER GATE ADDED - COPY/PASTE READY

import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  Suspense,
  lazy,
} from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { apiJSON, getActiveBranch, setBranch, unlockBranch } from "./apiBase";

import NavBar from "./components/NavBar";
import PushNotificationToggle from "./components/PushNotificationToggle";
import TableMetadataModal from "./TableMetadataModal";
import SelectContactsModal from "./SelectContactsModal";
import TableEditor from "./TableEditor";
import SearchByDate from "./SearchByDate";
import LiveTableFeed from "./LiveTableFeed";
import BirthdaySearch from "./BirthdaySearch";
import GuestSearch from "./GuestSearch";
import MergeByDateAll from "./pages/MergeByDateAll";

import { RefreshProvider, useRefresh } from "./context/RefreshContext";
import { useAuth } from "./context/AuthContext";
import PermGate from "./components/PermGate";

import "./App.css";

import LiveTableLogsByBranch from "./LiveTableLogsByBranch";
import Reservations from "./components/Reservations";
import IntaCompetitions from "./IntaCompetitions";
import ChatSeenContacts from "./components/ChatSeenContacts";

const Login = lazy(() => import("./pages/Login"));
const AllData = lazy(() => import("./AllData"));
const MenuPdfPriceEditor = lazy(() => import("./MenuPdfPriceEditor"));
const MenuPriceMapCalibrator = lazy(() => import("./MenuPriceMapCalibrator"));

const ERPHome = lazy(() => import("./ERPHome"));
const ERPInventory = lazy(() => import("./ERPInventory"));
const ERPCentralKitchen = lazy(() => import("./ERPCentralKitchen"));
const ERPAccounting = lazy(() => import("./ERPAccounting"));

/* =========================
   LocalStorage keys
========================= */
const LS_ERP_UNLOCK = "erp:unlock:v1";
const LS_WORKSPACE = "workspace:choice:v1";

const LS_SECTIONS = "home:sections:v1";
const LS_SHORTCUT_SIZE = "home:shortcutSize:v1";
const LS_THEME_MODE = "home:themeMode:v1";
const LS_CUSTOM_BG = "home:customBg:v1";
const LS_SECTION_DISPLAY_MODE = "home:sectionDisplay:v1";

const LS_MUTE = "app:mute:v1";

/* =========================
   Branches
========================= */
const BRANCH_OPTIONS = [
  { value: "", label: "Auto (token/host)" },
  { value: "jounieh", label: "Jounieh" },
  { value: "tripoli", label: "Tripoli" },
  { value: "batroun", label: "Batroun" },
  { value: "zgharta", label: "Zgharta" },
  { value: "ehden", label: "Ehden" },
];

function normalizeUserBranchValues(raw) {
  const all = BRANCH_OPTIONS.filter((x) => x.value).map((x) => x.value);
  const arr = Array.isArray(raw)
    ? raw
    : String(raw || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

  const cleaned = arr.map((x) => String(x || "").trim().toLowerCase());
  if (cleaned.includes("*")) return all;
  return all.filter((b) => cleaned.includes(b));
}

/* =========================
   Default sections
========================= */
const DEFAULT_SECTIONS = [
  { id: "data-group", label: "Live Table Feed", enabled: true },
  { id: "insta", label: "Insta Competitions", enabled: true },
  { id: "reservations", label: "Reservations", enabled: true },
  { id: "table-logs", label: "All Branch Logs", enabled: true },
];

/* =========================
   Utilities
========================= */
function safeLSGet(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : v;
  } catch {
    return fallback;
  }
}

function safeLSSet(key, val) {
  try {
    localStorage.setItem(key, val);
  } catch {}
}

function safeJSONParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function getWorkspaceLabel(pathname) {
  if (pathname.startsWith("/erp")) return "ERP";
  if (pathname.startsWith("/home")) return "Home";
  if (pathname.startsWith("/birthdays")) return "Birthdays";
  if (pathname.startsWith("/guest-search")) return "Guest Search";
  if (pathname.startsWith("/reservations")) return "Reservations";
  if (pathname.startsWith("/competitions")) return "Competitions";
  if (pathname.startsWith("/chat-seen")) return "Chat Seen Contacts";
  if (pathname.startsWith("/all")) return "All Data";
  if (pathname.startsWith("/search-date")) return "Search By Date";
  if (pathname.startsWith("/merge-by-date-all")) return "Merge By Date";
  if (pathname.startsWith("/menu-price-editor")) return "Menu Price Editor";
  if (pathname.startsWith("/menu-price-calibrator")) return "Menu Calibrator";
  if (pathname.startsWith("/control")) return "Control";
  return "App";
}

function dispatchNotificationRefresh(reason = "unknown", extra = {}) {
  try {
    window.dispatchEvent(
      new CustomEvent("app:notifications:refresh", {
        detail: { reason, ts: Date.now(), ...extra },
      })
    );
  } catch {}

  try {
    window.dispatchEvent(
      new CustomEvent("bamboo:notifications", {
        detail: { reason, ts: Date.now(), ...extra },
      })
    );
  } catch {}
}

/* =========================
   Global Server Loading Gate
========================= */
function ServerGate({ children }) {
  const [serverStatus, setServerStatus] = useState("checking");
  const [lastChecked, setLastChecked] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  const checkServer = useCallback(async () => {
    try {
      await apiJSON("/health", { method: "GET" });
      setServerStatus("online");
      setLastChecked(new Date().toLocaleTimeString());
    } catch (err) {
      console.error("Server connection failed:", err);
      setServerStatus("offline");
      setRetryCount((v) => v + 1);
      setLastChecked(new Date().toLocaleTimeString());
    }
  }, []);

  useEffect(() => {
    checkServer();

    const interval = setInterval(() => {
      checkServer();
    }, 5000);

    const handleOnline = () => checkServer();
    const handleFocus = () => checkServer();

    window.addEventListener("online", handleOnline);
    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("focus", handleFocus);
    };
  }, [checkServer]);

  if (serverStatus !== "online") {
    const isChecking = serverStatus === "checking";

    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100%",
          position: "fixed",
          inset: 0,
          zIndex: 999999,
          background:
            "radial-gradient(circle at top, rgba(34,197,94,0.18), transparent 35%), linear-gradient(135deg, #020617 0%, #0f172a 55%, #111827 100%)",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          fontFamily:
            "Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 460,
            textAlign: "center",
            padding: "34px 24px",
            borderRadius: 28,
            background: "rgba(15,23,42,0.82)",
            border: "1px solid rgba(148,163,184,0.22)",
            boxShadow: "0 30px 90px rgba(0,0,0,0.42)",
            backdropFilter: "blur(18px)",
          }}
        >
          <div
            style={{
              width: 92,
              height: 92,
              margin: "0 auto 24px",
              borderRadius: "50%",
              border: "7px solid rgba(255,255,255,0.12)",
              borderTopColor: "#22c55e",
              animation: "serverSpin 1s linear infinite",
            }}
          />

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "7px 13px",
              borderRadius: 999,
              background: isChecking
                ? "rgba(245,158,11,0.14)"
                : "rgba(239,68,68,0.14)",
              color: isChecking ? "#fbbf24" : "#fca5a5",
              fontSize: 13,
              fontWeight: 700,
              marginBottom: 14,
            }}
          >
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                background: isChecking ? "#f59e0b" : "#ef4444",
                boxShadow: `0 0 14px ${isChecking ? "#f59e0b" : "#ef4444"}`,
              }}
            />
            {isChecking ? "Checking server" : "Server offline"}
          </div>

          <h1
            style={{
              margin: "0 0 10px",
              fontSize: 28,
              lineHeight: 1.2,
              fontWeight: 800,
              letterSpacing: "-0.03em",
            }}
          >
            {isChecking
              ? "Connecting to Bamboo system..."
              : "Waiting for server connection"}
          </h1>

          <p
            style={{
              margin: "0 auto",
              maxWidth: 370,
              lineHeight: 1.65,
              fontSize: 15,
              color: "rgba(226,232,240,0.82)",
            }}
          >
            {isChecking
              ? "Please wait while the app checks the backend connection."
              : "The system is trying to reconnect automatically. Keep this screen open."}
          </p>

          <div
            style={{
              marginTop: 22,
              display: "grid",
              gap: 10,
              gridTemplateColumns: "1fr",
            }}
          >
            <button
              type="button"
              onClick={checkServer}
              style={{
                width: "100%",
                padding: "13px 18px",
                borderRadius: 16,
                border: "none",
                background: "linear-gradient(135deg, #22c55e, #16a34a)",
                color: "white",
                fontWeight: 800,
                fontSize: 15,
                cursor: "pointer",
                boxShadow: "0 14px 30px rgba(34,197,94,0.25)",
              }}
            >
              🔄 Retry Connection
            </button>

            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                width: "100%",
                padding: "12px 18px",
                borderRadius: 16,
                border: "1px solid rgba(148,163,184,0.35)",
                background: "rgba(15,23,42,0.65)",
                color: "rgba(255,255,255,0.9)",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              ↻ Reload App
            </button>
          </div>

          <div
            style={{
              marginTop: 18,
              fontSize: 12,
              color: "rgba(203,213,225,0.62)",
              lineHeight: 1.6,
            }}
          >
            <div>Automatic retry every 5 seconds</div>
            {lastChecked && <div>Last check: {lastChecked}</div>}
            {retryCount > 0 && <div>Reconnect attempts: {retryCount}</div>}
          </div>
        </div>

        <style>
          {`
            @keyframes serverSpin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return children;
}

/* =========================
   API status light
========================= */
function ApiStatusLight({ small = false }) {
  const [status, setStatus] = useState("checking");

  const checkApi = useCallback(async () => {
    try {
      await apiJSON("/saved-tables", { method: "GET" });
      setStatus("online");
    } catch (err) {
      console.error("API status check failed:", err);
      setStatus("offline");
    }
  }, []);

  useEffect(() => {
    checkApi();
    const id = setInterval(checkApi, 20000);
    return () => clearInterval(id);
  }, [checkApi]);

  const color =
    status === "online"
      ? "#22c55e"
      : status === "offline"
      ? "#ef4444"
      : "#f59e0b";

  const text =
    status === "online"
      ? "Server connected"
      : status === "offline"
      ? "Server offline"
      : "Checking server";

  return (
    <div
      className={`api-status-pill ${small ? "small" : ""}`}
      title={text}
      aria-label={text}
    >
      <span
        className="api-status-dot"
        style={{
          background: color,
          boxShadow: `0 0 12px ${color}`,
        }}
      />
      <span>{text}</span>
    </div>
  );
}

/* =========================
   Error Boundary
========================= */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: error?.message || "Something went wrong.",
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("AppErrorBoundary:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="page"
          style={{
            minHeight: "100vh",
            display: "grid",
            placeItems: "center",
            padding: 20,
          }}
        >
          <div className="error-panel">
            <h2 style={{ marginTop: 0 }}>⚠️ Application error</h2>
            <p style={{ opacity: 0.9 }}>
              A screen crashed while loading. Reload the app to continue.
            </p>
            <pre className="error-pre">{this.state.message}</pre>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="btn primary"
                onClick={this.handleReload}
              >
                🔄 Reload app
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/* =========================
   Auth / Perm wrappers
========================= */
function RequireAuth({ children }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  return children;
}

function WithPerm({ perm, children }) {
  return (
    <PermGate
      perm={perm}
      fallback={
        <div className="page page-denied">
          <h2>⛔ 403 — Not allowed</h2>
          <p>You do not have permission to open this page.</p>
        </div>
      }
    >
      {children}
    </PermGate>
  );
}

function RequireErpCode({ children }) {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      setAllowed(false);
      return;
    }

    const unlocked = localStorage.getItem(LS_ERP_UNLOCK) === "1";
    if (unlocked) {
      setAllowed(true);
      return;
    }

    const code = window.prompt("Enter CK / ERP access code:");
    if (code === "veru@123") {
      localStorage.setItem(LS_ERP_UNLOCK, "1");
      setAllowed(true);
    } else {
      alert("❌ Wrong code. Access denied.");
      localStorage.removeItem(LS_ERP_UNLOCK);
      localStorage.removeItem(LS_WORKSPACE);
      navigate("/", { replace: true });
    }
  }, [navigate]);

  if (!allowed) {
    return (
      <div className="page">
        <p>Checking ERP access…</p>
      </div>
    );
  }

  return children;
}

function ProtectedRoute({ perm, erp = false, children }) {
  if (erp) {
    return (
      <RequireAuth>
        <WithPerm perm={perm}>
          <RequireErpCode>{children}</RequireErpCode>
        </WithPerm>
      </RequireAuth>
    );
  }

  if (perm) {
    return (
      <RequireAuth>
        <WithPerm perm={perm}>{children}</WithPerm>
      </RequireAuth>
    );
  }

  return <RequireAuth>{children}</RequireAuth>;
}

/* =========================
   Small pages
========================= */
function Control() {
  return (
    <div className="page">
      <h1>Control Panel</h1>
      <p>Use the NavBar → More menu for sync options and settings.</p>
    </div>
  );
}

function AppSelector() {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ws = localStorage.getItem(LS_WORKSPACE);
    if (ws === "contacts") navigate("/home", { replace: true });
    else if (ws === "erp") navigate("/erp", { replace: true });
  }, [navigate]);

  const handleOpenHome = useCallback(() => {
    safeLSSet(LS_WORKSPACE, "contacts");
    navigate("/home");
  }, [navigate]);

  const handleChooseInventory = useCallback(() => {
    const unlocked = safeLSGet(LS_ERP_UNLOCK, "") === "1";
    let ok = unlocked;

    if (!unlocked) {
      const code = window.prompt("Enter CK / ERP access code:");
      if (code === "veru@123") {
        safeLSSet(LS_ERP_UNLOCK, "1");
        ok = true;
      } else {
        alert("❌ Wrong code. Access denied.");
        return;
      }
    }

    if (ok) {
      safeLSSet(LS_WORKSPACE, "erp");
      navigate("/erp");
    }
  }, [navigate]);

  return (
    <div className="page app-selector">
      <div className="selector-panel">
        <div className="selector-topline">
          <span className="selector-brand-pill">Bamboo Workspace</span>
          <ApiStatusLight />
        </div>

        <h1 className="selector-title">Choose where you want to go</h1>
        <p className="selector-subtitle">
          Open the main Home workspace for daily operations, or go to ERP /
          Inventory for stock and accounting.
        </p>

        <div className="selector-grid">
          <button
            type="button"
            onClick={handleOpenHome}
            className="selector-card selector-card-home"
          >
            <div className="selector-card-emoji">🏠</div>
            <div className="selector-card-title">Open Home</div>
            <div className="selector-card-text">
              Tables, guests, birthdays, WhatsApp sending, live feed, and
              day-to-day branch tools.
            </div>
          </button>

          <button
            type="button"
            onClick={handleChooseInventory}
            className="selector-card selector-card-erp"
          >
            <div className="selector-card-emoji">🧾</div>
            <div className="selector-card-title">Open ERP / Inventory</div>
            <div className="selector-card-text">
              Warehouses, central kitchen, items, stock flow, and accounting.
            </div>
          </button>
        </div>

        <div className="selector-help">
          <span>Tip:</span> your last selected workspace opens automatically next
          time.
        </div>
      </div>
    </div>
  );
}

/* =========================
   Shared modal shell
========================= */
function ModalShell({ title, onClose, compact = false, children }) {
  return (
    <div className="modal-overlay">
      <div className={`modal-content ${compact ? "compact" : ""}`}>
        <div className="modal-shell-head">
          <h3 className="modal-shell-title">{title}</h3>
          <button className="btn ghost small" onClick={onClose} type="button">
            ❌ Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SavedTablesModal({ open, tables, onClose, onLoad, onRefresh }) {
  if (!open) return null;

  return (
    <ModalShell title="📂 Saved Tables" compact onClose={onClose}>
      <div className="table-button-grid">
        {tables.length === 0 && (
          <div className="empty-hint">
            No saved tables found yet. Create a new table first.
          </div>
        )}

        {tables.map((name) => (
          <button
            key={name}
            className="table-btn"
            onClick={() => onLoad(name)}
            type="button"
          >
            {name}
          </button>
        ))}
      </div>

      <div className="modal-action-row">
        <button className="btn ghost" onClick={onClose} type="button">
          ❌ Close
        </button>
        <button className="btn primary" onClick={onRefresh} type="button">
          🔄 Refresh list
        </button>
      </div>
    </ModalShell>
  );
}

/* =========================
   CustomizeModal
========================= */
function CustomizeModal({
  open,
  onClose,
  sectionDisplayMode,
  setSectionDisplayMode,
  shortcutSize,
  setShortcutSize,
  themeMode,
  setThemeMode,
  customBg,
  setCustomBg,
  muted,
  setMuted,
  sections,
  moveSection,
  toggleSectionEnabled,
  handleCompactDb,
  isCompacting,
}) {
  if (!open) return null;

  return (
    <ModalShell title="⚙️ Customize Home Screen" onClose={onClose}>
      <div
        className="customize-body"
        style={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <div className="customize-row">
          <span className="customize-label">Section opening style</span>
          <div className="customize-controls">
            <button
              type="button"
              className={
                sectionDisplayMode === "window"
                  ? "btn primary small"
                  : "btn ghost small"
              }
              onClick={() => setSectionDisplayMode("window")}
            >
              🖼 Full screen
            </button>
            <button
              type="button"
              className={
                sectionDisplayMode === "inline"
                  ? "btn primary small"
                  : "btn ghost small"
              }
              onClick={() => setSectionDisplayMode("inline")}
            >
              📄 Inline
            </button>
          </div>
        </div>

        <div className="customize-row">
          <span className="customize-label">Shortcut size</span>
          <div className="customize-controls">
            {["small", "medium", "large"].map((size) => (
              <button
                key={size}
                type="button"
                className={
                  shortcutSize === size ? "btn primary small" : "btn ghost small"
                }
                onClick={() => setShortcutSize(size)}
              >
                {size[0].toUpperCase() + size.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="customize-row">
          <span className="customize-label">Theme</span>
          <div className="customize-controls">
            {[
              { id: "dark", label: "🌌 Dark" },
              { id: "forest", label: "🌿 Forest" },
              { id: "neon", label: "💜 Neon" },
              { id: "custom", label: "🎨 Custom" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                className={
                  themeMode === t.id ? "btn primary small" : "btn ghost small"
                }
                onClick={() => setThemeMode(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {themeMode === "custom" && (
          <div className="customize-row">
            <span className="customize-label">Background color</span>
            <div className="customize-controls">
              <input
                type="color"
                value={customBg}
                onChange={(e) => setCustomBg(e.target.value)}
                style={{
                  width: 44,
                  height: 28,
                  borderRadius: 999,
                  border: "1px solid rgba(148, 163, 184, 0.6)",
                  background: "transparent",
                  padding: 0,
                }}
              />
              <span style={{ fontSize: 11, opacity: 0.8 }}>
                Pick any background color
              </span>
            </div>
          </div>
        )}

        <div className="customize-row">
          <span className="customize-label">Alerts</span>
          <div className="customize-controls">
            <button
              type="button"
              className={muted ? "btn primary small" : "btn ghost small"}
              onClick={() => setMuted(true)}
            >
              🔇 Muted
            </button>
            <button
              type="button"
              className={!muted ? "btn primary small" : "btn ghost small"}
              onClick={() => setMuted(false)}
            >
              🔊 Sound ON
            </button>

            <span style={{ fontSize: 11, opacity: 0.75, marginLeft: 8 }}>
              Saved only on this device.
            </span>
          </div>
        </div>

        <div className="customize-row customize-sections-row">
          <span className="customize-label">Sections order</span>
          <div className="customize-sections-list">
            {sections.map((s, idx) => (
              <div key={s.id} className="customize-section-item">
                <span
                  className="customize-section-label"
                  style={{
                    opacity: s.enabled ? 1 : 0.45,
                    textDecoration: s.enabled ? "none" : "line-through",
                  }}
                >
                  {String(s.label ?? "")}
                </span>
                <div className="customize-section-buttons">
                  <button
                    type="button"
                    className="btn ghost small"
                    onClick={() => moveSection(s.id, -1)}
                    disabled={idx === 0}
                  >
                    ⬆
                  </button>
                  <button
                    type="button"
                    className="btn ghost small"
                    onClick={() => moveSection(s.id, +1)}
                    disabled={idx === sections.length - 1}
                  >
                    ⬇
                  </button>
                  <button
                    type="button"
                    className={s.enabled ? "btn ghost small" : "btn primary small"}
                    onClick={() => toggleSectionEnabled(s.id)}
                  >
                    {s.enabled ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="customize-row">
          <span className="customize-label">Database</span>
          <div className="customize-controls">
            <button
              type="button"
              className="btn primary small"
              onClick={handleCompactDb}
              disabled={isCompacting}
            >
              {isCompacting ? "🗜 Compacting…" : "🗜 Compact contacts.db"}
            </button>
            <span style={{ fontSize: 11, opacity: 0.7, marginLeft: 8 }}>
              Runs compact_db_clone.py on the backend.
            </span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 8, textAlign: "right" }}>
        <button className="btn primary small" onClick={onClose} type="button">
          ✅ Done
        </button>
      </div>
    </ModalShell>
  );
}

/* =========================
   Collapsible
========================= */
function Collapsible({
  id,
  title,
  defaultCollapsed = true,
  externalOpenKey,
  onOpenSectionWindow,
  displayMode = "inline",
  children,
}) {
  const isWindowMode = displayMode === "window";
  const [collapsed, setCollapsed] = useState(
    isWindowMode ? true : !!defaultCollapsed
  );
  const innerRef = useRef(null);
  const [maxH, setMaxH] = useState(0);

  useEffect(() => {
    if (isWindowMode) {
      setCollapsed(true);
      setMaxH(0);
    }
  }, [isWindowMode]);

  useEffect(() => {
    if (!id || isWindowMode) return;
    if (externalOpenKey && externalOpenKey === id) setCollapsed(false);
  }, [externalOpenKey, id, isWindowMode]);

  useEffect(() => {
    if (isWindowMode) return;
    const el = innerRef.current;
    if (!el) return;
    setMaxH(collapsed ? 0 : (el.scrollHeight || 0) + 8);
  }, [collapsed, children, isWindowMode]);

  useEffect(() => {
    if (isWindowMode) return;
    const el = innerRef.current;
    if (!el || collapsed) return;
    if (typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(() => {
      const target = innerRef.current;
      if (!target) return;
      setMaxH((target.scrollHeight || 0) + 8);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsed, isWindowMode]);

  const handleExpandClick = () => {
    if (isWindowMode) {
      onOpenSectionWindow?.(id);
      return;
    }
    setCollapsed((v) => !v);
  };

  const computedMaxH = isWindowMode ? 0 : maxH;

  return (
    <section className="card collapsible" style={{ marginTop: 12 }} id={id}>
      <div className="card-header collapsible-header">
        <h2 className="collapsible-title">{title}</h2>
        <button
          type="button"
          className="btn collapsible-toggle"
          aria-expanded={!collapsed}
          onClick={handleExpandClick}
        >
          {isWindowMode ? "🖼 Open" : collapsed ? "➕ Open" : "➖ Close"}
        </button>
      </div>

      <div
        className="collapsible-body"
        style={{
          overflow: "hidden",
          maxHeight: `${computedMaxH}px`,
          transition: isWindowMode ? "none" : "max-height 260ms ease",
        }}
      >
        <div
          ref={innerRef}
          className="collapsible-inner"
          style={{ padding: "8px 12px" }}
        >
          {children}
        </div>
      </div>
    </section>
  );
}

/* =========================
   Home
========================= */
function Home() {
  const { refreshKey, triggerRefresh } = useRefresh();
  const { user } = useAuth();
  const location = useLocation();

  const [notice, setNotice] = useState("");
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  const noticeTimerRef = useRef(null);

  const [now, setNow] = useState(new Date());

  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [tableMeta, setTableMeta] = useState(null);

  const [tableData, setTableData] = useState(null);
  const [activeTable, setActiveTable] = useState(null);

  const [showTablesModal, setShowTablesModal] = useState(false);
  const [savedTables, setSavedTables] = useState([]);

  const [showEodReminder, setShowEodReminder] = useState(false);
  const SNOOZE_KEY = "eod:snoozeUntil";

  const [sectionDisplayMode, setSectionDisplayMode] = useState(() =>
    safeLSGet(LS_SECTION_DISPLAY_MODE, "window")
  );

  const [windowSection, setWindowSection] = useState(null);
  const [activeSection, setActiveSection] = useState(null);

  const [sections, setSections] = useState(() => {
    const raw = safeLSGet(LS_SECTIONS, "");
    if (!raw) return DEFAULT_SECTIONS;

    const parsed = safeJSONParse(raw, null);
    if (!Array.isArray(parsed) || !parsed.length) return DEFAULT_SECTIONS;

    const map = new Map(parsed.map((s) => [s.id, s]));
    return DEFAULT_SECTIONS.map((d) => {
      const saved = map.get(d.id) || {};
      return { ...d, ...saved, label: saved.label ?? d.label };
    });
  });

  const [shortcutSize, setShortcutSize] = useState(() =>
    safeLSGet(LS_SHORTCUT_SIZE, "medium")
  );

  const [themeMode, setThemeMode] = useState(() =>
    safeLSGet(LS_THEME_MODE, "dark")
  );

  const [customBg, setCustomBg] = useState(() =>
    safeLSGet(LS_CUSTOM_BG, "#020617")
  );

  const [showCustomize, setShowCustomize] = useState(false);
  const [isCompacting, setIsCompacting] = useState(false);

  const [muted, setMuted] = useState(() => safeLSGet(LS_MUTE, "0") === "1");

  const [activeBranch, setActiveBranchState] = useState(
    () => getActiveBranch() || ""
  );

  const allowedBranchValues = useMemo(
    () => normalizeUserBranchValues(user?.branches),
    [user?.branches]
  );
  const visibleBranchOptions = useMemo(
    () => BRANCH_OPTIONS.filter((b) => b.value && allowedBranchValues.includes(b.value)),
    [allowedBranchValues.join("|")]
  );

  const sectionRegistry = useMemo(
    () => ({
      "data-group": {
        id: "data-group",
        title: "Live Table Feed",
        render: () => <LiveTableFeed />,
      },
      insta: {
        id: "insta",
        title: "Insta Competitions",
        render: () => (
          <Suspense fallback="Loading…">
            <IntaCompetitions />
          </Suspense>
        ),
      },
      reservations: {
        id: "reservations",
        title: "Reservations",
        render: () => <Reservations />,
      },
      "table-logs": {
        id: "table-logs",
        title: "Live Table Logs (All Branches)",
        render: () => <LiveTableLogsByBranch />,
      },
    }),
    []
  );

  const notify = useCallback((msg) => {
    setNotice(msg);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(""), 3000);
  }, []);

  useEffect(() => {
    document.title = `Bamboo • ${getWorkspaceLabel(location.pathname)}`;
  }, [location.pathname]);

  useEffect(() => {
    const sync = () => setActiveBranchState(getActiveBranch() || "");
    window.addEventListener("storage", sync);
    window.addEventListener("bamboo:branch", sync);
    window.addEventListener("app:branch", sync);

    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("bamboo:branch", sync);
      window.removeEventListener("app:branch", sync);
    };
  }, []);

  const setActiveBranch = useCallback((branchValue) => {
    const requested = String(branchValue || "").trim().toLowerCase();
    if (!requested || !allowedBranchValues.includes(requested)) return;

    if (allowedBranchValues.length > 1) {
      try {
        unlockBranch({ reload: false });
      } catch {}
    }

    setBranch(requested, { reload: false, force: true });
    const nextBranch = getActiveBranch() || requested;
    setActiveBranchState(nextBranch);
    dispatchNotificationRefresh("branch-change", { branch: nextBranch });
  }, [allowedBranchValues.join("|")]);

  useEffect(() => {
    if (!allowedBranchValues.length) return;
    const current = String(getActiveBranch() || "").trim().toLowerCase();
    if (allowedBranchValues.includes(current)) return;
    setActiveBranch(allowedBranchValues[0]);
  }, [allowedBranchValues.join("|"), setActiveBranch]);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      notify("🌐 Back online");
    };
    const onOffline = () => {
      setIsOnline(false);
      notify("📴 You are offline");
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [notify]);

  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    };
  }, []);

  useEffect(() => safeLSSet(LS_SECTIONS, JSON.stringify(sections)), [sections]);
  useEffect(() => safeLSSet(LS_SHORTCUT_SIZE, shortcutSize), [shortcutSize]);
  useEffect(() => safeLSSet(LS_THEME_MODE, themeMode), [themeMode]);
  useEffect(() => safeLSSet(LS_CUSTOM_BG, customBg), [customBg]);
  useEffect(
    () => safeLSSet(LS_SECTION_DISPLAY_MODE, sectionDisplayMode),
    [sectionDisplayMode]
  );

  useEffect(() => {
    safeLSSet(LS_MUTE, muted ? "1" : "0");
    try {
      window.dispatchEvent(
        new CustomEvent("app:mute-changed", { detail: { muted } })
      );
    } catch {}
  }, [muted]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    dispatchNotificationRefresh("home-mounted", {
      branch: activeBranch || getActiveBranch() || "",
    });
  }, []);

  useEffect(() => {
    dispatchNotificationRefresh("home-refresh-key", {
      refreshKey,
      branch: activeBranch || getActiveBranch() || "",
    });
  }, [refreshKey, activeBranch]);

  const isAfter2315 = useMemo(() => {
    const hh = now.getHours();
    const mm = now.getMinutes();
    return (hh === 23 && mm >= 15) || hh > 23;
  }, [now]);

  useEffect(() => {
    const snoozeUntil = Number(sessionStorage.getItem(SNOOZE_KEY) || "0");
    setShowEodReminder(isAfter2315 && Date.now() > snoozeUntil);
  }, [isAfter2315, now]);

  const snoozeEod = useCallback((mins = 10) => {
    const until = Date.now() + mins * 60 * 1000;
    sessionStorage.setItem(SNOOZE_KEY, String(until));
    setShowEodReminder(false);
  }, []);

  const fetchSavedTables = useCallback(async () => {
    try {
      const data = await apiJSON("/saved-tables", { method: "GET" });
      setSavedTables(Array.isArray(data?.tables) ? data.tables : []);
    } catch (err) {
      console.error("Failed to fetch saved tables:", err);
      setSavedTables([]);
      notify("❌ Failed to load saved tables");
    }
  }, [notify]);

  useEffect(() => {
    fetchSavedTables();
  }, [fetchSavedTables, refreshKey, activeBranch]);

  const handleLoadTable = useCallback(
    async (tableName) => {
      try {
        const data = await apiJSON(`/table/${encodeURIComponent(tableName)}`, {
          method: "GET",
        });

        if (data?.error) {
          alert(`❌ Failed to load table: ${data.error}`);
          return;
        }

        setActiveTable(tableName);
        setTableData(data);
        setShowTablesModal(false);
        dispatchNotificationRefresh("table-opened", {
          tableName,
          branch: activeBranch || getActiveBranch() || "",
        });
      } catch (err) {
        console.error("Failed to load table:", err);
        alert("❌ Failed to load table");
      }
    },
    [activeBranch]
  );

  const mergeAllTables = useCallback(async () => {
    if (!window.confirm("Merge all saved tables into one table.db?")) return;

    try {
      const data = await apiJSON("/merge-tables", { method: "POST" });
      alert(data?.message || "✅ Merge complete");
      triggerRefresh();
      dispatchNotificationRefresh("merge-tables-success", {
        branch: activeBranch || getActiveBranch() || "",
      });
    } catch (err) {
      console.error("Failed to merge tables:", err);
      alert("❌ Failed to merge tables.");
      dispatchNotificationRefresh("merge-tables-failed", {
        branch: activeBranch || getActiveBranch() || "",
      });
    }
  }, [triggerRefresh, activeBranch]);

  const runEndOfDay = useCallback(async () => {
    if (!isAfter2315) {
      notify("⏳ End of Day is available after 23:15.");
      return;
    }
    if (!window.confirm("Run End of Day now?")) return;

    try {
      const data = await apiJSON("/end-of-day", { method: "POST" });
      alert(
        `✅ End of Day complete\nInserted: ${data?.inserted ?? 0}\nSkipped: ${
          data?.skipped ?? 0
        }`
      );
      triggerRefresh();
      snoozeEod(240);
      dispatchNotificationRefresh("end-of-day-success", {
        branch: activeBranch || getActiveBranch() || "",
      });
    } catch (e) {
      console.error("EOD error:", e);
      alert("❌ Network error running End of Day.");
      dispatchNotificationRefresh("end-of-day-failed", {
        branch: activeBranch || getActiveBranch() || "",
      });
    }
  }, [isAfter2315, notify, snoozeEod, triggerRefresh, activeBranch]);

  const handleCompactDb = useCallback(async () => {
    if (
      !window.confirm(
        "Compact contacts.db on the server now?\n\nThis may lock the DB for a short time."
      )
    ) {
      return;
    }

    setIsCompacting(true);

    try {
      const data = await apiJSON("/compact-db", { method: "POST" });

      const before = data?.before_size_h || data?.before_size || "";
      const after = data?.after_size_h || data?.after_size || "";
      const saved = data?.saved_size_h || data?.saved_size || "";

      if (before && after) {
        notify(
          `✅ Compacted DB: ${before} → ${after}${
            saved ? ` (saved ${saved})` : ""
          }`
        );
      } else {
        notify(data?.message || "✅ Database compacted");
      }

      triggerRefresh();
      dispatchNotificationRefresh("compact-db-success", {
        branch: activeBranch || getActiveBranch() || "",
      });
    } catch (err) {
      console.error("Compact DB failed:", err);
      notify("❌ Failed to compact DB");
      dispatchNotificationRefresh("compact-db-failed", {
        branch: activeBranch || getActiveBranch() || "",
      });
    } finally {
      setIsCompacting(false);
    }
  }, [notify, triggerRefresh, activeBranch]);

  const openSectionWindow = useCallback(
    (sectionId) => {
      if (sectionDisplayMode !== "window") return;
      setWindowSection(sectionId);
    },
    [sectionDisplayMode]
  );

  const openSectionFromShortcut = useCallback(
    (sectionId) => {
      if (sectionDisplayMode === "inline") setActiveSection(sectionId);
      else setWindowSection(sectionId);
    },
    [sectionDisplayMode]
  );

  const getWindowTitle = useCallback(
    (sectionId) => {
      const found = sections.find((s) => s.id === sectionId);
      return found?.label || sectionRegistry[sectionId]?.title || "";
    },
    [sections, sectionRegistry]
  );

  const renderSectionComponent = useCallback(
    (sectionId) => sectionRegistry[sectionId]?.render?.() ?? null,
    [sectionRegistry]
  );

  const moveSection = useCallback((id, direction) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === id);
      if (idx === -1) return prev;

      const nextIndex = idx + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;

      const copy = [...prev];
      const [item] = copy.splice(idx, 1);
      copy.splice(nextIndex, 0, item);
      return copy;
    });
  }, []);

  const toggleSectionEnabled = useCallback((id) => {
    setSections((prev) =>
      prev.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s))
    );
  }, []);

  const appRootClass = `app-root theme-${themeMode}`;
  const appRootStyle =
    themeMode === "custom" ? { background: customBg } : undefined;

  const visibleSections = useMemo(
    () => sections.filter((s) => s.enabled),
    [sections]
  );

  useEffect(() => {
    if (sectionDisplayMode === "window") setActiveSection(null);
    if (sectionDisplayMode === "inline") setWindowSection(null);
  }, [sectionDisplayMode]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const target = e.target;
      const tag = target?.tagName?.toLowerCase?.();
      const isTyping =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        target?.isContentEditable;

      if (isTyping) return;

      if (e.key === "Escape") {
        if (windowSection) setWindowSection(null);
        else if (showCustomize) setShowCustomize(false);
        else if (showTablesModal) setShowTablesModal(false);
        else if (showMetadataModal) setShowMetadataModal(false);
      }

      if (e.altKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        setShowMetadataModal(true);
      }

      if (e.altKey && e.key.toLowerCase() === "t") {
        e.preventDefault();
        setShowTablesModal(true);
      }

      if (e.altKey && e.key.toLowerCase() === "r") {
        e.preventDefault();
        triggerRefresh();
        notify("🔄 Refreshed");
        dispatchNotificationRefresh("keyboard-refresh", {
          branch: activeBranch || getActiveBranch() || "",
        });
      }

      if (e.altKey && e.key.toLowerCase() === "c") {
        e.preventDefault();
        setShowCustomize((v) => !v);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    windowSection,
    showCustomize,
    showTablesModal,
    showMetadataModal,
    triggerRefresh,
    notify,
    activeBranch,
  ]);

  return (
    <div className={appRootClass} style={appRootStyle}>
      <NavBar
        onCreateNewTable={() => setShowMetadataModal(true)}
        onOpenTables={() => setShowTablesModal(true)}
        onMergeAll={mergeAllTables}
        onEndOfDay={runEndOfDay}
        currentTableName={activeTable}
      />

      <div className="app-container">
        <div className="container">
          {!isOnline && (
            <div className="offline-banner">
              📡 You are currently offline. Some actions may fail until the
              connection returns.
            </div>
          )}

          {showEodReminder && (
            <div className="eod-reminder">
              <span>
                ⏰ Do not forget to run <b>End of Day</b> before leaving.
              </span>
              <div className="eod-actions">
                <button
                  className="btn"
                  onClick={() => snoozeEod(10)}
                  type="button"
                >
                  😴 Snooze 10m
                </button>
                <button
                  className="btn primary"
                  onClick={runEndOfDay}
                  type="button"
                >
                  ▶ Run now
                </button>
              </div>
            </div>
          )}

          <div className="home-hero card">
            <div className="home-hero-content">
              <div>
                <div className="home-hero-badge">Bamboo Home</div>
                <h1 className="home-hero-title">Welcome back</h1>
                <p className="home-hero-text">
                  Manage tables, track activity, switch branches, and open the
                  tools you need faster.
                </p>
              </div>

              <div
                className="home-hero-actions"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  alignItems: "flex-end",
                }}
              >
                <PushNotificationToggle />

                <button
                  type="button"
                  className="btn primary"
                  onClick={() => setShowMetadataModal(true)}
                >
                  ➕ New Table
                </button>

                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => setShowTablesModal(true)}
                >
                  📂 Open Saved Tables
                </button>
              </div>
            </div>
          </div>

          <div className="toolbar-card card">
            <div className="toolbar-card-inner">
              <div className="toolbar-group">
                <label className="toolbar-label">Branch</label>

                <select
                  value={activeBranch}
                  onChange={(e) => {
                    setActiveBranch(e.target.value);
                    triggerRefresh();
                  }}
                  className="toolbar-select"
                >
                  {visibleBranchOptions.map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>

                <span className="toolbar-chip">
                  {activeBranch ? `Active: ${activeBranch}` : "Select branch"}
                </span>

                <ApiStatusLight small />
              </div>

              <div className="toolbar-group toolbar-actions">
                <button
                  type="button"
                  className="btn ghost small"
                  onClick={() => {
                    triggerRefresh();
                    notify("🔄 Refreshed");
                    dispatchNotificationRefresh("toolbar-refresh", {
                      branch: activeBranch || getActiveBranch() || "",
                    });
                  }}
                >
                  🔄 Refresh
                </button>

                <button
                  type="button"
                  className="btn ghost small"
                  onClick={() => setShowCustomize(true)}
                >
                  ⚙️ Customize
                </button>
              </div>
            </div>
          </div>

          <div className="shortcut-grid">
            {visibleSections.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`card shortcut-card shortcut-${shortcutSize}`}
                onClick={() => openSectionFromShortcut(s.id)}
              >
                <div className="shortcut-title">{String(s.label ?? "")}</div>
                <div className="shortcut-sub">
                  Tap to open {String(s.label ?? "")}
                </div>
              </button>
            ))}

            {visibleSections.length === 0 && (
              <div className="empty-hint">
                No shortcuts available. Enable sections from Customize.
              </div>
            )}
          </div>

          {sectionDisplayMode === "inline" && activeSection && (
            <div style={{ marginTop: 12 }}>
              <Collapsible
                id={activeSection}
                title={getWindowTitle(activeSection)}
                defaultCollapsed={false}
                externalOpenKey={activeSection}
                displayMode="inline"
                onOpenSectionWindow={openSectionWindow}
              >
                {renderSectionComponent(activeSection)}
              </Collapsible>
            </div>
          )}
        </div>

        {showMetadataModal && (
          <TableMetadataModal
            onSubmit={(meta) => {
              setTableMeta(meta);
              setShowMetadataModal(false);
            }}
            onClose={() => setShowMetadataModal(false)}
          />
        )}

        {tableMeta && (
          <SelectContactsModal
            key={`${
              tableMeta.table_number || tableMeta.table_name || "table"
            }-${refreshKey}`}
            tableMeta={tableMeta}
            onClose={() => {
              setTableMeta(null);
              triggerRefresh();
              dispatchNotificationRefresh("select-contacts-closed", {
                table_number:
                  tableMeta?.table_number || tableMeta?.table_name || "",
                branch: activeBranch || getActiveBranch() || "",
              });
            }}
          />
        )}

        {tableData && (
          <div className="modal-overlay editor-overlay">
            <div className="modal-content fullscreen-editor">
              <TableEditor
                tableName={activeTable}
                initialData={tableData}
                onClose={() => {
                  setActiveTable(null);
                  setTableData(null);
                  triggerRefresh();
                  dispatchNotificationRefresh("table-editor-closed", {
                    tableName: activeTable || "",
                    branch: activeBranch || getActiveBranch() || "",
                  });
                }}
              />
            </div>
          </div>
        )}

        <SavedTablesModal
          open={showTablesModal}
          tables={savedTables}
          onClose={() => setShowTablesModal(false)}
          onLoad={handleLoadTable}
          onRefresh={fetchSavedTables}
        />

        <CustomizeModal
          open={showCustomize}
          onClose={() => setShowCustomize(false)}
          sectionDisplayMode={sectionDisplayMode}
          setSectionDisplayMode={setSectionDisplayMode}
          shortcutSize={shortcutSize}
          setShortcutSize={setShortcutSize}
          themeMode={themeMode}
          setThemeMode={setThemeMode}
          customBg={customBg}
          setCustomBg={setCustomBg}
          muted={muted}
          setMuted={setMuted}
          sections={sections}
          moveSection={moveSection}
          toggleSectionEnabled={toggleSectionEnabled}
          handleCompactDb={handleCompactDb}
          isCompacting={isCompacting}
        />

        {windowSection && (
          <div className="modal-overlay editor-overlay">
            <div className="modal-content fullscreen-editor">
              <div className="window-head">
                <h3 style={{ margin: 0 }}>{getWindowTitle(windowSection)}</h3>
                <button
                  className="btn ghost small"
                  onClick={() => setWindowSection(null)}
                  type="button"
                >
                  ❌ Close
                </button>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                {renderSectionComponent(windowSection)}
              </div>
            </div>
          </div>
        )}

        {notice && <div className="toast">{notice}</div>}
      </div>
    </div>
  );
}

/* =========================
   Route table
========================= */
function AppRoutes() {
  // Remount the active route whenever the selected branch changes.
  // This prevents branch-aware screens from keeping stale Jounieh data after
  // an admin switches to Batroun/Tripoli/Zgharta/Ehden.
  const [routeBranch, setRouteBranch] = useState(() => getActiveBranch() || "");

  useEffect(() => {
    const syncBranch = () => setRouteBranch(getActiveBranch() || "");
    window.addEventListener("bamboo:branch", syncBranch);
    window.addEventListener("app:branch", syncBranch);
    return () => {
      window.removeEventListener("bamboo:branch", syncBranch);
      window.removeEventListener("app:branch", syncBranch);
    };
  }, []);

  const appRoutes = [
    {
      path: "/",
      element: <AppSelector />,
    },
    {
      path: "/home",
      perm: "nav_home",
      element: <Home />,
    },
    {
      path: "/all",
      perm: "nav_all_data",
      element: <AllData />,
    },
    {
      path: "/search-date",
      perm: "nav_search_date",
      element: <SearchByDate />,
    },
    {
      path: "/merge-by-date-all",
      perm: "nav_merge_by_date_all",
      element: <MergeByDateAll />,
    },
    {
      path: "/reservations",
      perm: "nav_reservations",
      element: <Reservations />,
    },
    {
      path: "/birthdays",
      perm: "nav_birthdays",
      element: <BirthdaySearch />,
    },
    {
      path: "/guest-search",
      perm: "nav_guest_search",
      element: <GuestSearch />,
    },
    {
  path: "/competitions",
  perm: "nav_competitions",
  element: <IntaCompetitions />,
},

{
  path: "/chat-seen",
  perm: "nav_control",
  element: <ChatSeenContacts />,
},

{
  path: "/control",
  perm: "nav_control",
  element: <Control />,
},
    {
      path: "/menu-price-editor",
      perm: "nav_menu_prices",
      element: <MenuPdfPriceEditor />,
    },
    {
      path: "/menu-price-calibrator",
      perm: "nav_menu_calibrator",
      element: <MenuPriceMapCalibrator />,
    },
    {
      path: "/erp",
      perm: "nav_inventory",
      erp: true,
      element: <ERPHome />,
    },
    {
      path: "/erp/inventory",
      perm: "nav_inventory",
      erp: true,
      element: <ERPInventory />,
    },
    {
      path: "/erp/ck",
      perm: "nav_inventory",
      erp: true,
      element: <ERPCentralKitchen />,
    },
    {
      path: "/erp/accounting",
      perm: "nav_inventory",
      erp: true,
      element: <ERPAccounting />,
    },
  ];

  return (
    <Routes key={`active-branch:${routeBranch || "none"}`}>
      {appRoutes.map((r) => (
        <Route
          key={r.path}
          path={r.path}
          element={
            <ProtectedRoute perm={r.perm} erp={r.erp}>
              {r.element}
            </ProtectedRoute>
          }
        />
      ))}

      <Route path="/login" element={<Login />} />

      <Route
        path="*"
        element={
          <RequireAuth>
            <Navigate to="/" replace />
          </RequireAuth>
        }
      />
    </Routes>
  );
}

/* =========================
   Root App
========================= */
function App() {
  return (
    <AppErrorBoundary>
      <ServerGate>
        <RefreshProvider>
          <Router>
            <Suspense
              fallback={
                <div
                  style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#020617",
                    color: "white",
                    fontSize: 18,
                    fontWeight: 700,
                  }}
                >
                  Loading application...
                </div>
              }
            >
              <AppRoutes />
            </Suspense>
          </Router>
        </RefreshProvider>
      </ServerGate>
    </AppErrorBoundary>
  );
}

export default App;